import { NodeSelection, TextSelection } from '@tiptap/pm/state';
import { mergeAttributes } from '@tiptap/core';
import HorizontalRule from '@tiptap/extension-horizontal-rule';

export interface PageBreakOptions {
  HTMLAttributes: Record<string, any>;
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    /**
     * base tiptap setHorizontalRule `<hr />`
     */
    horizontalRule: {
      setHorizontalRule: () => ReturnType;
    };
    /**
     * insert tncms pageBreak - `<hr class="tncms-page-break">`
     */
    pageBreak: {
      setPageBreak: () => ReturnType;
    };
  }
}

/**
 * Custom Page Break extension for Tiptap that allows inserting
 * page breaks with a specific class for TNCMS.
 *
 * <hr class="tncms-page-break"> and basic <hr />
 *
 * This extension extends the HorizontalRule and adds a class
 * attribute to differentiate it from regular horizontal rules.
 *
 * If using this extension, ensure to exclude the original
 * HorizontalRule extension from the StarterKit, the primary
 * use of this extension is in useCreateEditor 'body' type
 * for the main article editor toolbar.
 *
 */

export const HorizontalRuleMulti = HorizontalRule.extend<PageBreakOptions>({
  // Keep the same node name as tiptap so stock commands work
  // as well as extending for our custom tncms page breaks
  name: 'horizontalRule',

  // Nodes are draggable
  draggable: true,

  // do not default all hr to 'tncms-page-break'
  // only nodes we explicitly insert get the class
  // on load parseHTML will should read the class
  // and decide the render tag per priority
  addAttributes() {
    return {
      class: {
        default: null, // only set class when we insert it
        parseHTML: (el) => el.getAttribute('class'),
      },
    };
  },

  // Instruct ProseMirror how to parse incoming HTML:
  // Prioritze class="tncms-page-break" then
  // fallback to the basic <hr> tag
  parseHTML() {
    return [
      { tag: 'hr.tncms-page-break', priority: 100 },
      { tag: 'hr', priority: 10 },
    ];
  },

  // When serializing back to HTML, add the stored class
  renderHTML({ HTMLAttributes }) {
    return ['hr', mergeAttributes(HTMLAttributes)];
  },

  // Commands: keep the original setHorizontalRule,
  // and add setPageBreak on the same node type
  addCommands() {
    return {
      // spread in base HorizontalRule commands
      ...this?.parent?.(),
      // our custom tncms page break command
      setPageBreak:
        () =>
        ({ chain, state }) => {
          const { $to } = state.selection;
          const chaincmd = chain();
          // insert at start of block vs inline
          if ($to.parentOffset === 0) {
            chaincmd.insertContentAt(Math.max($to.pos - 2, 0), {
              type: this.name,
              attrs: { class: 'tncms-page-break' },
            });
          } else {
            chaincmd.insertContent({
              type: this.name,
              attrs: { class: 'tncms-page-break' },
            });
          }
          // then move the cursor after it
          return chaincmd
            .command(({ tr, dispatch }) => {
              if (dispatch) {
                const { $to: newPos } = tr.selection;
                const posAfter = newPos.end();

                if (newPos.nodeAfter) {
                  if (newPos.nodeAfter.isTextblock) {
                    tr.setSelection(TextSelection.create(tr.doc, newPos.pos + 1));
                  } else if (newPos.nodeAfter.isBlock) {
                    tr.setSelection(NodeSelection.create(tr.doc, newPos.pos));
                  } else {
                    tr.setSelection(TextSelection.create(tr.doc, newPos.pos));
                  }
                } else {
                  // end of doc - insert a paragraph
                  const node = newPos.parent.type.contentMatch.defaultType?.create();
                  if (node) {
                    tr.insert(posAfter, node);
                    tr.setSelection(TextSelection.create(tr.doc, posAfter + 1));
                  }
                }
                tr.scrollIntoView();
              }
              return true;
            })
            .run();
        },
    };
  },
});
